import {
  Component,
  useEffect,
  useMemo,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react"
import { useNavigate } from "react-router"
import { Button } from "@stayframe/ui"
import { AiLoadingCard } from "@/components/AiLoadingCard"
import { FeedbackTrigger } from "@/components/feedback"
import { cn } from "@/lib/utils"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { useIntelligenceGate } from "@/lib/useIntelligenceGate"
import { useActivationEvent } from "@/lib/useActivationEvent"
import { usePlanContext } from "@/lib/usePlanContext"
import {
  apiClient,
  type AirbnbCategoryScores,
  type BookingCategoryScores,
  type BookingReviewItem,
  type BookingReviewSummary,
  type ReviewAnalysis,
  type ReviewIntelligenceResponse,
  type ReviewNumericContext,
  type ReviewRisk,
  type ReviewStrength,
} from "@/lib/api"
import {
  deriveTrend,
  formatRecencyDays,
  formatReviewDate,
  formatTimeAgo,
  guestTypeSummary,
  themeDisplayName,
  type ReviewItem,
  type ReviewSource,
  type ReviewSummary,
  useReviewsPageData,
  velocityMonthly,
} from "./reviews/useReviewsData"
import {
  AlertTriangle,
  Check,
  ExternalLink,
  Link2,
  Loader2,
  MessageSquareText,
  Pencil,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Star,
  TrendingDown,
  TrendingUp,
  X,
} from "lucide-react"

const REVIEWS_AI_MESSAGES = [
  "Reading recent guest feedback…",
  "Comparing Airbnb and Booking.com sentiment…",
  "Ranking what guests reliably love…",
  "Pulling risk patterns into focus…",
  "Shaping your reputation brief…",
]

type ReviewsTone = "positive" | "warning" | "critical" | "neutral"
type VerdictTone = "strong" | "steady" | "softening" | "mixed"
type SentimentTrend = "improving" | "stable" | "softening" | "declining"

type ActionTarget =
  | { type: "route"; href: string }
  | { type: "section"; sectionId: string }
  | { type: "recompute" }
  | { type: "reimport"; platform: "airbnb" | "booking" }

interface ActionItem {
  key: string
  category: string
  title: string
  detail: string
  consequence: string
  priority: number
  tone: ReviewsTone
  ctaLabel: string
  target: ActionTarget
}

interface HeroMetric {
  key: string
  label: string
  value: string
  detail: string
  context?: string
  tone?: ReviewsTone
  sectionId?: string
}

interface VerdictModel {
  headline: string
  summary: string
  tone: VerdictTone
}

interface SectionReadout {
  headline: string
  summary: string
}

interface PlatformModel {
  key: "airbnb" | "booking"
  label: string
  ratingValue: string
  ratingScale: string
  normalizedRating: number | null
  reviewCount: number
  reviewLabel: string
  recencyLabel: string
  velocityLabel: string
  trendLabel: string
  trendTone: ReviewsTone
  commentary: string
  statusLabel: string
  statusTone: ReviewsTone
}

interface ChangeItem {
  key: string
  label: string
  value: string
  detail: string
  tone: ReviewsTone
}

interface NoticeItem {
  key: string
  tone: "warning" | "critical"
  title: string
  body: string
  action?: string
  onClick?: () => void
}

interface EvidenceReview {
  key: string
  source: "airbnb" | "booking"
  reviewer: string
  date: string
  rating: string
  quote: string
  note?: string
  meta?: string
}

interface CategoryEntry {
  label: string
  score: number
}

function surfaceHref(propertyId: string, surface: string): string {
  return `/properties/${propertyId}/${surface}`
}

function safeDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const normalized = value.trim()
  if (!normalized) return null
  const date = /^\d{4}-\d{2}-\d{2}$/.test(normalized)
    ? new Date(`${normalized}T12:00:00`)
    : new Date(normalized)
  if (Number.isNaN(date.getTime())) return null
  return date
}

function formatRelativeTime(value: string | null | undefined): string {
  const date = safeDate(value)
  if (!date) return "Unknown"
  const diffMs = Date.now() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)
  if (diffMin < 1) return "just now"
  if (diffMin < 60) return `${diffMin} min ago`
  if (diffHr < 24) return `${diffHr} hrs ago`
  if (diffDay < 7) return `${diffDay} ${diffDay === 1 ? "day" : "days"} ago`
  if (diffDay < 30) return `${Math.floor(diffDay / 7)} wks ago`
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

function pluralize(count: number, singular: string, plural?: string): string {
  const p = plural ?? (/(ch|sh|s|x|z)$/i.test(singular) ? `${singular}es` : `${singular}s`)
  return `${count} ${count === 1 ? singular : p}`
}

function formatBandLabel(value: string | null | undefined): string {
  if (!value) return "Pending"
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function formatSignedPoints(value: number | null | undefined, suffix = "pts"): string | null {
  if (value == null || !Number.isFinite(value)) return null
  const rounded = Number(value.toFixed(1))
  return `${value > 0 ? "+" : ""}${rounded}${suffix}`
}

function normalizeBookingRating(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value)) return null
  return value / 2
}

function toneFromStatus(status: string | null | undefined): ReviewsTone {
  const next = (status || "").toLowerCase()
  if (next === "error" || next === "stale") return "critical"
  if (next === "running" || next === "pending") return "warning"
  return "positive"
}

function toneFromTrend(value: string | null | undefined): ReviewsTone {
  if (value === "improving") return "positive"
  if (value === "softening" || value === "declining") return "warning"
  return "neutral"
}

function toneClass(tone: ReviewsTone): string {
  if (tone === "positive") return "rvw-chip--positive"
  if (tone === "warning") return "rvw-chip--warning"
  if (tone === "critical") return "rvw-chip--critical"
  return "rvw-chip--neutral"
}

function trendLabel(value: string | null | undefined): string {
  if (value === "improving") return "Strengthening"
  if (value === "softening" || value === "declining") return "Softening"
  return "Stable"
}

function lastReviewAt(
  airbnbReviews: ReviewItem[] | null,
  bookingReviews: BookingReviewItem[] | null,
): string | null {
  const candidates = [
    ...(airbnbReviews || []).map((review) => review.created_at),
    ...(bookingReviews || []).map((review) => review.created_at),
  ]
    .map((value) => safeDate(value))
    .filter((value): value is Date => Boolean(value))
  if (candidates.length === 0) return null
  const newest = candidates.sort((a, b) => b.getTime() - a.getTime())[0]
  return newest.toISOString()
}

function recencyLabelFromContext(
  numericContext: ReviewNumericContext | undefined,
  fallbackTimestamp: string | null,
): string {
  if (numericContext?.newest_review_days != null) return formatRecencyDays(numericContext.newest_review_days)
  if (fallbackTimestamp) return formatRelativeTime(fallbackTimestamp)
  return "No recent review"
}

function velocityLabel(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value <= 0) return "No recent review flow"
  return `${velocityMonthly(value)}/mo`
}

function computeNormalizedAverage(options: {
  airbnbRating: number | null
  airbnbCount: number
  bookingRating: number | null
  bookingCount: number
}): number | null {
  const points: Array<{ rating: number; count: number }> = []
  if (options.airbnbRating != null && options.airbnbCount > 0) points.push({ rating: options.airbnbRating, count: options.airbnbCount })
  if (options.bookingRating != null && options.bookingCount > 0) points.push({ rating: options.bookingRating, count: options.bookingCount })
  const totalCount = points.reduce((sum, item) => sum + item.count, 0)
  if (totalCount === 0) return null
  return points.reduce((sum, item) => sum + item.rating * item.count, 0) / totalCount
}

function themeRoute(propertyId: string, theme: string): string | null {
  const key = theme.toLowerCase()
  if (/(communication|check[_ -]?in|arrival|clarity|access|instruction|door|guidebook)/.test(key)) {
    return surfaceHref(propertyId, "guidebook")
  }
  if (/(rule|quiet|checkout|check[_ -]?out|house)/.test(key)) {
    return surfaceHref(propertyId, "house-rules")
  }
  if (/(value|comfort|accuracy|listing|location|facilities|wifi|expectation)/.test(key)) {
    return surfaceHref(propertyId, "listings")
  }
  return null
}

function normalizeThemeKey(value: string | null | undefined): string {
  return (value || "").toLowerCase().replace(/[^a-z0-9]+/g, "")
}

function findThemeSignal(
  themes: ReviewAnalysis["themes"] | undefined,
  theme: string | null | undefined,
) {
  if (!themes || !theme) return null
  if (themes[theme]) return themes[theme]
  const target = normalizeThemeKey(theme)
  const entry = Object.entries(themes).find(([key]) => normalizeThemeKey(key) === target)
  return entry?.[1] || null
}

function buildScoreState(score: number | null | undefined, band: string | null | undefined) {
  if (score == null || !Number.isFinite(score)) {
    return {
      label: "Pending read",
      context: "Stayframe needs enough synchronized reviews before the composite score becomes meaningful.",
      tone: "neutral" as ReviewsTone,
    }
  }

  if (score < 55) {
    return {
      label: "At risk",
      context: `${formatBandLabel(band)} band. Trust is weak enough that the next negative review can drag the story quickly.`,
      tone: "critical" as ReviewsTone,
    }
  }

  if (score < 70) {
    return {
      label: "Below target",
      context: `${formatBandLabel(band)} band. Stability here is not comfort; guest trust still needs repair work.`,
      tone: "warning" as ReviewsTone,
    }
  }

  if (score < 82) {
    return {
      label: "Holding",
      context: `${formatBandLabel(band)} band. Protect consistency before one recurring issue changes the tone.`,
      tone: "neutral" as ReviewsTone,
    }
  }

  return {
    label: "Strong trust",
    context: `${formatBandLabel(band)} band. The current guest story is landing well enough to protect rather than repair.`,
    tone: "positive" as ReviewsTone,
  }
}

function buildFreshnessState(options: {
  stale: boolean | null | undefined
  computedAt: string | null | undefined
}) {
  if (options.stale) {
    return {
      value: "Needs refresh",
      detail: "New reviews landed after the last intelligence run.",
      tone: "warning" as ReviewsTone,
    }
  }

  if (!options.computedAt) {
    return {
      value: "Pending",
      detail: "The first full reputation brief has not been generated yet.",
      tone: "neutral" as ReviewsTone,
    }
  }

  const computedAt = safeDate(options.computedAt)
  const ageDays = computedAt ? (Date.now() - computedAt.getTime()) / 86400000 : 0
  return {
    value: formatRelativeTime(options.computedAt),
    detail: ageDays >= 5 ? "Analysis is aging" : "Analysis is current",
    tone: ageDays >= 5 ? ("warning" as ReviewsTone) : ("positive" as ReviewsTone),
  }
}

function buildVerdict(options: {
  hasIntel: boolean
  totalReviewCount: number
  temperatureBand: string | null | undefined
  overallTrend: SentimentTrend | null
  topStrength: ReviewStrength | null
  topRisk: ReviewRisk | null
  platformSpread: number | null
  bookingSofter: boolean
  airbnbCount: number
  bookingCount: number
  latestReviewLabel: string
  crossPlatformNote?: string | null
}): VerdictModel {
  const {
    hasIntel,
    totalReviewCount,
    temperatureBand,
    overallTrend,
    topStrength,
    topRisk,
    platformSpread,
    bookingSofter,
    airbnbCount,
    bookingCount,
    latestReviewLabel,
    crossPlatformNote,
  } = options

  if (totalReviewCount < 3) {
    return {
      headline: "Review volume is still too thin for a confident trust read.",
      summary: `${pluralize(totalReviewCount, "review")} are in so far. Stayframe needs a little more signal before it can separate a real pattern from noise.`,
      tone: "mixed",
    }
  }

  if (!hasIntel) {
    return {
      headline: "Guest feedback is in, but the reputation brief is still catching up.",
      summary: `${pluralize(totalReviewCount, "review")} are available, with the latest ${latestReviewLabel.toLowerCase()}. Recompute intelligence to rank what matters most.`,
      tone: "steady",
    }
  }

  if (airbnbCount > 0 && bookingCount > 0 && platformSpread != null && platformSpread >= 0.45) {
    return {
      headline: bookingSofter
        ? "Booking.com is softer than Airbnb, which points to expectation mismatch."
        : "Airbnb is softer than Booking.com, suggesting the stay story is not landing evenly.",
      summary: crossPlatformNote
        || `The platforms are separated by ${platformSpread.toFixed(1)} normalized stars, so this looks more like a channel-specific trust gap than a universal service decline.`,
      tone: "mixed",
    }
  }

  if ((overallTrend === "softening" || overallTrend === "declining") && topRisk) {
    return {
      headline: `Recent reviews are softening, with ${themeDisplayName(topRisk.theme).toLowerCase()} creating drag.`,
      summary: topRisk.signal,
      tone: "softening",
    }
  }

  if ((temperatureBand === "warm" || temperatureBand === "hot") && topStrength) {
    return {
      headline: `Reputation is ${temperatureBand} and steady, with ${themeDisplayName(topStrength.theme).toLowerCase()} driving trust.`,
      summary: topStrength.signal,
      tone: overallTrend === "improving" ? "strong" : "steady",
    }
  }

  if (overallTrend === "improving") {
    return {
      headline: "Guest sentiment is strengthening, and the stay is landing more confidently.",
      summary: topStrength?.signal || "Recent reviews are warmer than the earlier baseline.",
      tone: "strong",
    }
  }

  return {
    headline: "Reputation is holding, but the next consistency gap will shape the story.",
    summary: topRisk?.signal || topStrength?.signal || "Guest trust is broadly stable across recent reviews.",
    tone: "steady",
  }
}

function buildPlatformModels(options: {
  summary: ReviewSummary | null
  source: ReviewSource | null
  bookingSummary: BookingReviewSummary | null
  bookingSource: ReviewSource | null
  analysis: ReviewAnalysis | undefined
  airbnbLatest: string | null
  bookingLatest: string | null
}): PlatformModel[] {
  const models: PlatformModel[] = []
  const airbnbRating = options.summary?.average_rating ?? null
  const bookingRawRating = options.bookingSummary?.hotel_rating ?? options.bookingSummary?.computed_average ?? null

  if ((options.summary?.reviews_count ?? 0) > 0) {
    const trend = options.analysis?.sentiment_trend || "stable"
    models.push({
      key: "airbnb",
      label: "Airbnb",
      ratingValue: airbnbRating != null ? airbnbRating.toFixed(1) : "—",
      ratingScale: "/5",
      normalizedRating: airbnbRating,
      reviewCount: options.summary?.reviews_count ?? 0,
      reviewLabel: pluralize(options.summary?.reviews_count ?? 0, "review"),
      recencyLabel: recencyLabelFromContext(options.analysis?._numeric_context, options.airbnbLatest),
      velocityLabel: velocityLabel(options.analysis?._numeric_context?.review_velocity_per_quarter ?? null),
      trendLabel: trendLabel(trend),
      trendTone: toneFromTrend(trend),
      commentary: options.analysis?.sentiment_trend_explanation
        || options.analysis?.strengths[0]?.signal
        || "Airbnb feedback is available, but detailed commentary is still building.",
      statusLabel: options.source?.status === "ok"
        ? `Synced ${formatRelativeTime(options.source.last_synced_at)}`
        : options.source?.status === "running"
          ? "Import running"
          : options.source?.status === "stale"
            ? "Import stale"
            : options.source?.status === "error"
              ? "Import failed"
              : "Connected",
      statusTone: toneFromStatus(options.source?.status),
    })
  }

  if ((options.bookingSummary?.fetched_count ?? 0) > 0) {
    const trend = options.analysis?._booking_numeric_context ? deriveTrend(options.analysis._booking_numeric_context) : "stable"
    models.push({
      key: "booking",
      label: "Booking.com",
      ratingValue: bookingRawRating != null ? bookingRawRating.toFixed(1) : "—",
      ratingScale: "/10",
      normalizedRating: normalizeBookingRating(bookingRawRating),
      reviewCount: options.bookingSummary?.fetched_count ?? 0,
      reviewLabel: pluralize(options.bookingSummary?.fetched_count ?? 0, "review"),
      recencyLabel: recencyLabelFromContext(options.analysis?._booking_numeric_context, options.bookingLatest),
      velocityLabel: velocityLabel(options.analysis?._booking_numeric_context?.review_velocity_per_quarter ?? null),
      trendLabel: trendLabel(trend),
      trendTone: toneFromTrend(trend),
      commentary: options.analysis?.booking_insights?.category_summary
        || options.bookingSummary?.hotel_rating_label
        || "Booking.com feedback is available, but source-specific detail is still thin.",
      statusLabel: options.bookingSource?.status === "ok"
        ? `Synced ${formatRelativeTime(options.bookingSource.last_synced_at)}`
        : options.bookingSource?.status === "running"
          ? "Import running"
          : options.bookingSource?.status === "stale"
            ? "Import stale"
            : options.bookingSource?.status === "error"
              ? "Import failed"
              : "Connected",
      statusTone: toneFromStatus(options.bookingSource?.status),
    })
  }

  return models
}

function buildMomentumReadout(options: {
  analysis: ReviewAnalysis | undefined
  overallTrend: SentimentTrend | null
  platformModels: PlatformModel[]
  topRisk: ReviewRisk | null
  topStrength: ReviewStrength | null
  platformSpread: number | null
  bookingSofter: boolean
}): SectionReadout {
  const numeric = options.analysis?._numeric_context
  const bookingTrend = options.analysis?._booking_numeric_context
    ? deriveTrend(options.analysis._booking_numeric_context)
    : null
  const softeningPlatform = options.platformModels.find((platform) => platform.trendTone === "warning")
  const improvingPlatform = options.platformModels.find((platform) => platform.trendTone === "positive")

  if (!options.analysis) {
    return {
      headline: "Momentum will sharpen once the reputation brief is generated.",
      summary: "Stayframe needs the AI pass to compare recent review quality against the earlier baseline and separate noise from a real shift.",
    }
  }

  if ((options.overallTrend === "softening" || options.overallTrend === "declining") && softeningPlatform) {
    return {
      headline: `${softeningPlatform.label} is losing ground faster than the rest of the review story.`,
      summary:
        `${numeric?.trend_diff != null ? `${formatSignedPoints(numeric.trend_diff)} overall versus the earlier baseline. ` : ""}` +
        `${softeningPlatform.label} is the clearest source of drag right now, so this section is about change over time, not a static platform gap.`,
    }
  }

  if (bookingTrend === "softening" && options.platformSpread != null && options.platformSpread >= 0.45) {
    return {
      headline: options.bookingSofter
        ? "Booking.com is the channel where trust is actively softening."
        : "Airbnb is holding firmer while Booking.com drifts.",
      summary: "The spread is not just a static mismatch. The softer channel is also the one losing momentum, which is the earlier warning signal to act on.",
    }
  }

  if (options.overallTrend === "improving" && options.topStrength) {
    return {
      headline: `${themeDisplayName(options.topStrength.theme)} is helping recent sentiment strengthen.`,
      summary: `${options.topStrength.signal} The current job is to protect the experience guests are already rewarding.`,
    }
  }

  if ((options.overallTrend === "softening" || options.overallTrend === "declining") && options.topRisk) {
    return {
      headline: `${themeDisplayName(options.topRisk.theme)} is repeating enough to soften the recent review curve.`,
      summary: options.topRisk.evidence || options.topRisk.signal,
    }
  }

  if (improvingPlatform) {
    return {
      headline: `${improvingPlatform.label} is the clearest source of improvement right now.`,
      summary: `${improvingPlatform.label} is warmer than the earlier review window, which is the best signal that the guest story is not flat.`,
    }
  }

  return {
    headline: "Sentiment is stable, but it is not obviously strengthening yet.",
    summary:
      `${numeric?.trend_diff != null ? `${formatSignedPoints(numeric.trend_diff)} overall versus the earlier baseline. ` : ""}` +
      "Use the cards below to watch whether the next shift is a real recovery or the start of a softer platform-specific pattern.",
  }
}

function buildLoyaltyReadout(options: {
  loyaltyPct: number
  loyaltyTotal: number
  strength: "strong" | "moderate" | "weak" | "none" | null | undefined
  topRisk: ReviewRisk | null
  topStrength: ReviewStrength | null
}): SectionReadout {
  if (options.loyaltyTotal <= 0 || !options.strength || options.strength === "none") {
    return {
      headline: "Return intent is not surfacing strongly in review language yet.",
      summary: "Guests are not explicitly describing rebooking or recommendation often enough for this to act as a confident loyalty signal.",
    }
  }

  if (options.strength === "strong" && options.topStrength) {
    return {
      headline: "Guests are describing a stay they would repeat.",
      summary: `${options.loyaltyPct}% of review language points toward return or recommendation intent. Protect ${themeDisplayName(options.topStrength.theme).toLowerCase()} to keep that signal warm.`,
    }
  }

  if (options.strength === "weak" && options.topRisk) {
    return {
      headline: "Repeat intent is weak enough to treat as an early warning.",
      summary: `${options.loyaltyPct}% of review language suggests return or recommendation. Removing ${themeDisplayName(options.topRisk.theme).toLowerCase()} is the clearest way to lift it.`,
    }
  }

  return {
    headline: "Return intent is present, but not yet strong enough to relax.",
    summary: `${options.loyaltyPct}% of review language points toward return or recommendation. ${options.topRisk ? `Closing ${themeDisplayName(options.topRisk.theme).toLowerCase()} is the clearest lift.` : options.topStrength ? `Protect ${themeDisplayName(options.topStrength.theme).toLowerCase()} while the signal is still warm.` : "Use it as a watch signal rather than a comfort signal."}`,
  }
}

function buildActions(options: {
  propertyId: string
  source: ReviewSource | null
  bookingSource: ReviewSource | null
  hasIntel: boolean
  totalReviewCount: number
  intel: ReviewIntelligenceResponse | null
  topRisk: ReviewRisk | null
  topStrength: ReviewStrength | null
  platformSpread: number | null
  bookingSofter: boolean
  missingSecondSource: "airbnb" | "booking" | null
  loyaltyStrength: string | null
}): ActionItem[] {
  const actions: ActionItem[] = []

  const add = (action: ActionItem) => {
    if (actions.some((item) => item.key === action.key)) return
    actions.push(action)
  }

  if (options.source && (options.source.status === "error" || options.source.status === "stale")) {
    add({
      key: "airbnb_reimport",
      category: "Source health",
      title: "Airbnb reviews need a clean re-import",
      detail: options.source.last_error || "The Airbnb source is stale enough to lower confidence in the current read.",
      consequence: "Until Airbnb is refreshed, sentiment and review recency may understate current guest trust.",
      priority: 98,
      tone: "critical",
      ctaLabel: "Retry Airbnb",
      target: { type: "reimport", platform: "airbnb" },
    })
  }

  if (options.bookingSource && (options.bookingSource.status === "error" || options.bookingSource.status === "stale")) {
    add({
      key: "booking_reimport",
      category: "Source health",
      title: "Booking.com reviews need a clean re-import",
      detail: options.bookingSource.last_error || "The Booking.com source is stale enough to weaken the platform comparison.",
      consequence: "Until Booking.com is refreshed, the cross-platform trust story may be incomplete or misleading.",
      priority: 97,
      tone: "critical",
      ctaLabel: "Retry Booking",
      target: { type: "reimport", platform: "booking" },
    })
  }

  if (options.intel?.stale || (!options.hasIntel && options.totalReviewCount >= 3)) {
    add({
      key: "recompute",
      category: "AI intelligence",
      title: options.intel?.stale
        ? "New reviews are waiting to be folded into the reputation brief"
        : "Generate the first full reputation brief",
      detail: options.intel?.stale
        ? "Fresh reviews have landed since the last analysis."
        : "You have enough review volume for ranked strengths, risks, and platform synthesis.",
      consequence: "Without a fresh analysis, the page can describe the signal but not prioritize it sharply.",
      priority: 96,
      tone: "warning",
      ctaLabel: options.intel?.stale ? "Reanalyze now" : "Generate now",
      target: { type: "recompute" },
    })
  }

  if (options.topRisk) {
    const href = themeRoute(options.propertyId, options.topRisk.theme)
    add({
      key: "top_risk",
      category: "Guest friction",
      title: options.topRisk.signal,
      detail: options.topRisk.evidence || `Risk severity is ${options.topRisk.severity}.`,
      consequence: "If this keeps repeating, it will drag trust faster than the average score suggests.",
      priority: options.topRisk.severity === "high" ? 92 : 84,
      tone: options.topRisk.severity === "high" ? "critical" : "warning",
      ctaLabel: href
        ? href.includes("/guidebook")
          ? "Open Guidebook"
          : href.includes("/house-rules")
            ? "Open House Rules"
            : "Open Listings"
        : "Review evidence",
      target: href ? { type: "route", href } : { type: "section", sectionId: "rvw-evidence" },
    })
  }

  if (options.platformSpread != null && options.platformSpread >= 0.45) {
    add({
      key: "platform_spread",
      category: "Platform truth",
      title: options.bookingSofter
        ? "Booking.com expectations need a closer audit"
        : "Airbnb promise and delivery are drifting apart",
      detail: `The two platforms are separated by ${options.platformSpread.toFixed(1)} normalized stars.`,
      consequence: "Platform divergence usually means the stay promise is uneven, not that every guest is suddenly less happy.",
      priority: 88,
      tone: "warning",
      ctaLabel: "Open Listings",
      target: { type: "route", href: surfaceHref(options.propertyId, "listings") },
    })
  }

  if (options.missingSecondSource) {
    add({
      key: "missing_source",
      category: "Coverage",
      title: `Connect ${options.missingSecondSource === "airbnb" ? "Airbnb" : "Booking.com"} to see the full reputation picture`,
      detail: "A single source can tell you if reviews are warm or cold, but not whether the story changes by channel.",
      consequence: "Without both platforms, trust gaps can hide inside one booking source.",
      priority: 76,
      tone: "neutral",
      ctaLabel: "Review sources",
      target: { type: "section", sectionId: "rvw-sources" },
    })
  }

  if (!options.topRisk && options.topStrength) {
    const href = themeRoute(options.propertyId, options.topStrength.theme) || surfaceHref(options.propertyId, "listings")
    add({
      key: "protect_strength",
      category: "Protect what works",
      title: `Protect ${themeDisplayName(options.topStrength.theme).toLowerCase()} while sentiment is still warm`,
      detail: options.topStrength.signal,
      consequence: "The best-reviewed parts of the stay are usually the fastest way to preserve trust and repeat intent.",
      priority: options.loyaltyStrength === "strong" ? 80 : 68,
      tone: "positive",
      ctaLabel: href.includes("/guidebook") ? "Open Guidebook" : "Open Listings",
      target: { type: "route", href },
    })
  }

  if (actions.length === 0) {
    add({
      key: "steady_watch",
      category: "Reputation watch",
      title: "Guest trust is holding. Keep the experience consistent.",
      detail: "There is no immediate reputation fire, but consistency is what keeps the next review warm.",
      consequence: "A stable week is the right moment to protect the strengths guests already talk about.",
      priority: 50,
      tone: "positive",
      ctaLabel: "Review strengths",
      target: { type: "section", sectionId: "rvw-strengths" },
    })
  }

  return actions.sort((a, b) => b.priority - a.priority)
}

function buildChangeItems(options: {
  analysis: ReviewAnalysis | undefined
  intel: ReviewIntelligenceResponse | null
  latestReviewLabel: string
  platformSpread: number | null
  totalReviewCount: number
}): ChangeItem[] {
  const items: ChangeItem[] = []
  const numeric = options.analysis?._numeric_context
  const bookingNumeric = options.analysis?._booking_numeric_context

  if (numeric?.trend_diff != null) {
    items.push({
      key: "trend",
      label: "Momentum",
      value: formatSignedPoints(numeric.trend_diff) || "Stable",
      detail: options.analysis?.sentiment_trend_explanation || "Recent guest sentiment versus the earlier baseline.",
      tone: toneFromTrend(options.analysis?.sentiment_trend),
    })
  }

  if (numeric?.recent_window_count != null && numeric.prior_window_count != null) {
    const delta = numeric.recent_window_count - numeric.prior_window_count
    items.push({
      key: "recent_reviews",
      label: "Recent volume",
      value: delta === 0 ? `${numeric.recent_window_count}` : `${delta > 0 ? "+" : ""}${delta}`,
      detail: `${numeric.recent_window_count} reviews in the recent window vs ${numeric.prior_window_count} before it.`,
      tone: delta > 0 ? "positive" : delta < 0 ? "warning" : "neutral",
    })
  }

  if (options.platformSpread != null) {
    items.push({
      key: "spread",
      label: "Platform spread",
      value: options.platformSpread >= 0.1 ? `${options.platformSpread.toFixed(1)} stars` : "Aligned",
      detail: options.platformSpread >= 0.45
        ? "The channels are now telling meaningfully different trust stories."
        : "Airbnb and Booking.com are broadly aligned.",
      tone: options.platformSpread >= 0.45 ? "warning" : "positive",
    })
  }

  items.push({
    key: "recency",
    label: "Latest voice",
    value: options.latestReviewLabel,
    detail: `${pluralize(options.totalReviewCount, "review")} in the current pool.`,
    tone: "neutral",
  })

  if (bookingNumeric?.trend_diff != null) {
    items.push({
      key: "booking_trend",
      label: "Booking.com",
      value: formatSignedPoints(bookingNumeric.trend_diff) || "Stable",
      detail: "Source-specific recent sentiment movement.",
      tone: toneFromTrend(deriveTrend(bookingNumeric)),
    })
  }

  return items.slice(0, 4)
}

function buildEvidenceReviews(
  airbnbReviews: ReviewItem[] | null,
  bookingReviews: BookingReviewItem[] | null,
  analysis: ReviewAnalysis | undefined,
): EvidenceReview[] {
  const evidence: EvidenceReview[] = []
  const featured = analysis?.featured_reviews || []

  const airbnbFeatured = featured.filter((item) => !item.source || item.source === "airbnb")
  for (const feature of airbnbFeatured) {
    const match = (airbnbReviews || []).find((review) => {
      const firstName = (review.reviewer_name || "").split(/\s+/)[0].toLowerCase()
      const featureName = (feature.reviewer_name || "").toLowerCase()
      return firstName === featureName && review.created_at?.slice(0, 7) === feature.date?.slice(0, 7)
    })
    if (!match) continue
    evidence.push({
      key: `airbnb-${match.id}`,
      source: "airbnb",
      reviewer: match.reviewer_name || "Guest",
      date: formatReviewDate(match.created_at),
      rating: `${match.rating}/5`,
      quote: match.localized_text || match.text || "No written feedback available.",
      note: feature.reason,
    })
  }

  const bookingFeatured = featured.filter((item) => item.source === "booking")
  for (const feature of bookingFeatured) {
    const match = (bookingReviews || []).find((review) => {
      const firstName = (review.reviewer_name || "").split(/\s+/)[0].toLowerCase()
      const featureName = (feature.reviewer_name || "").toLowerCase()
      return firstName === featureName && review.created_at?.slice(0, 7) === feature.date?.slice(0, 7)
    })
    if (!match) continue
    evidence.push({
      key: `booking-${match.id}`,
      source: "booking",
      reviewer: match.reviewer_name || "Guest",
      date: formatReviewDate(match.created_at),
      rating: `${match.rating}/10`,
      quote: match.liked_text || match.disliked_text || match.review_title || "No written feedback available.",
      note: feature.reason,
      meta: [match.reviewer_location, match.traveler_type].filter(Boolean).join(" • "),
    })
  }

  if (evidence.length === 0) {
    for (const review of (airbnbReviews || []).slice(0, 2)) {
      evidence.push({
        key: `airbnb-${review.id}`,
        source: "airbnb",
        reviewer: review.reviewer_name || "Guest",
        date: formatReviewDate(review.created_at),
        rating: `${review.rating}/5`,
        quote: review.localized_text || review.text || "No written feedback available.",
      })
    }
    for (const review of (bookingReviews || []).filter((item) => item.liked_text || item.disliked_text).slice(0, 2)) {
      evidence.push({
        key: `booking-${review.id}`,
        source: "booking",
        reviewer: review.reviewer_name || "Guest",
        date: formatReviewDate(review.created_at),
        rating: `${review.rating}/10`,
        quote: review.liked_text || review.disliked_text || review.review_title || "No written feedback available.",
        meta: [review.reviewer_location, review.traveler_type].filter(Boolean).join(" • "),
      })
    }
  }

  return evidence.slice(0, 4)
}

function airbnbCategoryEntries(scores: AirbnbCategoryScores | undefined): CategoryEntry[] {
  if (!scores) return []
  const entries: Array<[string, number | null | undefined]> = [
    ["Accuracy", scores.accuracy],
    ["Check-in", scores.check_in],
    ["Cleanliness", scores.cleanliness],
    ["Communication", scores.communication],
    ["Location", scores.location],
    ["Value", scores.value],
  ]
  return entries.filter(([, score]) => score != null).map(([label, score]) => ({ label, score: score as number }))
}

function bookingCategoryEntries(scores: BookingCategoryScores | undefined): CategoryEntry[] {
  if (!scores) return []
  const entries: Array<[string, number | null | undefined]> = [
    ["Staff", scores.staff],
    ["Facilities", scores.facilities],
    ["Cleanliness", scores.cleanliness],
    ["Comfort", scores.comfort],
    ["Value", scores.value],
    ["Location", scores.location],
    ["WiFi", scores.wifi],
  ]
  return entries.filter(([, score]) => score != null).map(([label, score]) => ({ label, score: score as number }))
}

class ReviewsErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; message: string }> {
  state = { hasError: false, message: "" }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, message: error?.message || "Unknown reviews render failure" }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("ReviewsPage render failed", error, info)
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="rvw-page">
          <div className="rvw-shell">
            <div className="rvw-error">
              <p className="rvw-error__title">Reviews hit an unexpected data issue</p>
              <p className="rvw-error__body">
                A live data edge case interrupted the reputation dashboard render. Refresh and Stayframe will retry with safer fallbacks.
              </p>
              {this.state.message ? (
                <p className="rvw-error__body">
                  <code>{this.state.message}</code>
                </p>
              ) : null}
            </div>
          </div>
        </main>
      )
    }
    return this.props.children
  }
}

function ReviewsUpgradeWall({ propertyId }: { propertyId: string | undefined }) {
  const navigate = useNavigate()
  const { summary, bookingSummary, intel } = useReviewsPageData(propertyId)
  const airbnbCount = summary?.reviews_count ?? 0
  const bookingCount = bookingSummary?.fetched_count ?? 0
  const hasAny = airbnbCount > 0 || bookingCount > 0

  return (
    <main className="rvw-page">
      <div className="rvw-shell">
        <section className="rvw-upgrade">
          <div className="rvw-upgrade__hero">
            <span className="rvw-section-kicker">Reviews</span>
            <h1 className="rvw-empty__title">Unlock the reputation command center.</h1>
            <p className="rvw-empty__body">
              Stayframe already tracks your imported scores. Upgrade to turn guest feedback into ranked strengths, rising risks, loyalty signals, and platform-specific action.
            </p>
            <div className="rvw-empty__actions">
              <Button variant="primary" size="lg" onClick={() => navigate("settings?tab=billing")}>
                Upgrade to Insight
              </Button>
            </div>
          </div>

          {hasAny ? (
            <div className="rvw-upgrade__signals">
              <div className="rvw-kpi-grid">
                <div className="rvw-kpi">
                  <p className="rvw-kpi__label">Reputation score</p>
                  <p className="rvw-kpi__value">{intel?.reputation_score ?? "—"}</p>
                  <p className="rvw-kpi__detail">{formatBandLabel(intel?.temperature_band)} band</p>
                </div>
                <div className="rvw-kpi">
                  <p className="rvw-kpi__label">Airbnb</p>
                  <p className="rvw-kpi__value">{summary?.average_rating != null ? `${summary.average_rating.toFixed(1)}/5` : "—"}</p>
                  <p className="rvw-kpi__detail">{pluralize(airbnbCount, "review")}</p>
                </div>
                <div className="rvw-kpi">
                  <p className="rvw-kpi__label">Booking.com</p>
                  <p className="rvw-kpi__value">
                    {bookingSummary?.hotel_rating != null
                      ? `${bookingSummary.hotel_rating.toFixed(1)}/10`
                      : bookingSummary?.computed_average != null
                        ? `${bookingSummary.computed_average.toFixed(1)}/10`
                        : "—"}
                  </p>
                  <p className="rvw-kpi__detail">{pluralize(bookingCount, "review")}</p>
                </div>
              </div>
            </div>
          ) : null}
        </section>
      </div>
    </main>
  )
}

function SourceConnectCard({
  platform,
  placeholder,
  importing,
  value,
  onChange,
  onImport,
}: {
  platform: string
  placeholder: string
  importing: boolean
  value: string
  onChange: (value: string) => void
  onImport: () => void
}) {
  return (
    <div className="rvw-source-card rvw-source-card--empty">
      <div className="rvw-source-card__head">
        <div>
          <p className="rvw-source-card__platform">Connect {platform}</p>
          <p className="rvw-source-card__meta">Import the source to compare trust across channels.</p>
        </div>
      </div>
      <div className="rvw-source-card__editor">
        <input
          type="url"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && onImport()}
          placeholder={placeholder}
          disabled={importing}
        />
        <Button variant="primary" size="sm" onClick={onImport} disabled={importing || !value.trim()}>
          {importing ? "Importing…" : `Import ${platform}`}
        </Button>
      </div>
    </div>
  )
}

function SourceHealthCard({
  platform,
  source,
  reviewCount,
  importing,
  onReimport,
  onUrlUpdate,
}: {
  platform: string
  source: ReviewSource
  reviewCount: number
  importing: boolean
  onReimport: () => void
  onUrlUpdate: (url: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draftUrl, setDraftUrl] = useState(source.source_url)

  useEffect(() => {
    setDraftUrl(source.source_url)
  }, [source.source_url])

  const tone = toneFromStatus(source.status)
  const statusLabel = source.status === "ok"
    ? "Healthy"
    : source.status === "running"
      ? "Import running"
      : source.status === "stale"
        ? "Needs retry"
        : source.status === "error"
          ? "Import failed"
          : "Pending"

  return (
    <div className={cn("rvw-source-card", tone === "warning" && "rvw-source-card--warning", tone === "critical" && "rvw-source-card--critical")}>
      <div className="rvw-source-card__head">
        <div>
          <p className="rvw-source-card__platform">{platform}</p>
          <p className="rvw-source-card__meta">
            {pluralize(reviewCount, "review")} • {source.last_synced_at ? `Synced ${formatRelativeTime(source.last_synced_at)}` : "Sync time unavailable"}
          </p>
        </div>
        <span className={cn("rvw-chip", toneClass(tone))}>{statusLabel}</span>
      </div>

      {!editing ? (
        <div className="rvw-source-card__body">
          <div className="rvw-source-card__url">
            <Link2 className="h-3.5 w-3.5" />
            <span title={source.source_url}>{source.source_url}</span>
          </div>
          <div className="rvw-source-card__actions">
            <Button variant="secondary" size="sm" onClick={onReimport} loading={importing}>
              Re-import
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="h-3.5 w-3.5" />
              Edit URL
            </Button>
            <a
              href={source.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="rvw-inline-link"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open source
            </a>
          </div>
          {source.last_error ? <p className="rvw-source-card__error">{source.last_error}</p> : null}
        </div>
      ) : (
        <div className="rvw-source-card__editor">
          <input
            type="url"
            value={draftUrl}
            onChange={(event) => setDraftUrl(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setEditing(false)
              if (event.key === "Enter" && draftUrl.trim()) {
                onUrlUpdate(draftUrl.trim())
                setEditing(false)
              }
            }}
            disabled={importing}
          />
          <div className="rvw-source-card__actions">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (!draftUrl.trim()) return
                onUrlUpdate(draftUrl.trim())
                setEditing(false)
              }}
              disabled={importing || !draftUrl.trim()}
            >
              <Check className="h-3.5 w-3.5" />
              Save & re-import
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={importing}>
              <X className="h-3.5 w-3.5" />
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function ReviewsPageFull({ propertyId }: { propertyId: string | undefined }) {
  const navigate = useNavigate()
  const { property } = usePropertyContext()
  const {
    source,
    summary,
    reviews,
    bookingSource,
    bookingSummary,
    bookingReviews,
    intel,
    loading,
    error,
    setError,
    refresh,
    forceRecompute,
    recomputeCooldown,
  } = useReviewsPageData(propertyId)
  const { showRitual } = useIntelligenceGate({
    scope: "dashboard",
    propertyId,
    isReady: !loading,
  })
  useActivationEvent(propertyId, "reviews_viewed")

  const [airbnbUrlInput, setAirbnbUrlInput] = useState("")
  const [bookingUrlInput, setBookingUrlInput] = useState("")
  const [airbnbImporting, setAirbnbImporting] = useState(false)
  const [bookingImporting, setBookingImporting] = useState(false)
  const [recomputing, setRecomputing] = useState(false)
  const [showAllActions, setShowAllActions] = useState(false)

  const analysis = intel?.analysis
  const hasIntel = Boolean(intel?.available && analysis)
  const airbnbCount = summary?.reviews_count ?? 0
  const bookingCount = bookingSummary?.fetched_count ?? 0
  const totalReviewCount = airbnbCount + bookingCount
  const hasAirbnbData = airbnbCount > 0
  const hasBookingData = bookingCount > 0
  const hasAnyData = hasAirbnbData || hasBookingData
  const hasBothSources = hasAirbnbData && hasBookingData

  const airbnbAverage = summary?.average_rating ?? null
  const bookingAverageRaw = bookingSummary?.hotel_rating ?? bookingSummary?.computed_average ?? null
  const bookingAverageNormalized = normalizeBookingRating(bookingAverageRaw)
  const blendedRating = computeNormalizedAverage({
    airbnbRating: airbnbAverage,
    airbnbCount,
    bookingRating: bookingAverageNormalized,
    bookingCount,
  })

  const airbnbLatestAt = useMemo(() => {
    const latest = (reviews || [])
      .map((review) => safeDate(review.created_at))
      .filter((value): value is Date => Boolean(value))
      .sort((a, b) => b.getTime() - a.getTime())[0]
    return latest ? latest.toISOString() : null
  }, [reviews])

  const bookingLatestAt = useMemo(() => {
    const latest = (bookingReviews || [])
      .map((review) => safeDate(review.created_at))
      .filter((value): value is Date => Boolean(value))
      .sort((a, b) => b.getTime() - a.getTime())[0]
    return latest ? latest.toISOString() : null
  }, [bookingReviews])

  const latestReviewAt = useMemo(() => lastReviewAt(reviews, bookingReviews), [reviews, bookingReviews])
  const latestReviewLabel = analysis?._numeric_context?.newest_review_days != null || analysis?._booking_numeric_context?.newest_review_days != null
    ? (() => {
        const values = [
          analysis?._numeric_context?.newest_review_days,
          analysis?._booking_numeric_context?.newest_review_days,
        ].filter((value): value is number => typeof value === "number" && Number.isFinite(value))
        return values.length > 0 ? formatRecencyDays(Math.min(...values)) : (latestReviewAt ? formatRelativeTime(latestReviewAt) : "No recent review")
      })()
    : latestReviewAt
      ? formatRelativeTime(latestReviewAt)
      : "No recent review"

  const platformSpread = hasBothSources && airbnbAverage != null && bookingAverageNormalized != null
    ? Math.abs(airbnbAverage - bookingAverageNormalized)
    : null
  const bookingSofter = (bookingAverageNormalized ?? 0) < (airbnbAverage ?? 0)
  const overallTrend: SentimentTrend | null = hasIntel
    ? analysis?.sentiment_trend || "stable"
    : intel?.fallback?.rating_trend === "declining"
      ? "softening"
      : intel?.fallback?.rating_trend || null

  const topStrength = hasIntel ? (analysis?.strengths?.[0] || null) : null
  const topRisk = hasIntel ? (analysis?.risks?.[0] || null) : null
  const missingSecondSource = hasAirbnbData && !bookingSource
    ? "booking"
    : hasBookingData && !source
      ? "airbnb"
      : null

  const verdict = useMemo(
    () =>
      buildVerdict({
        hasIntel,
        totalReviewCount,
        temperatureBand: intel?.temperature_band,
        overallTrend,
        topStrength,
        topRisk,
        platformSpread,
        bookingSofter,
        airbnbCount,
        bookingCount,
        latestReviewLabel,
        crossPlatformNote: analysis?.cross_platform_note || null,
      }),
    [
      hasIntel,
      totalReviewCount,
      intel?.temperature_band,
      overallTrend,
      topStrength,
      topRisk,
      platformSpread,
      bookingSofter,
      airbnbCount,
      bookingCount,
      latestReviewLabel,
      analysis?.cross_platform_note,
    ],
  )

  const platformModels = useMemo(
    () =>
      buildPlatformModels({
        summary,
        source,
        bookingSummary,
        bookingSource,
        analysis,
        airbnbLatest: airbnbLatestAt,
        bookingLatest: bookingLatestAt,
      }),
    [summary, source, bookingSummary, bookingSource, analysis, airbnbLatestAt, bookingLatestAt],
  )
  const scoreState = useMemo(
    () => buildScoreState(intel?.reputation_score, intel?.temperature_band),
    [intel?.reputation_score, intel?.temperature_band],
  )
  const freshnessState = useMemo(
    () => buildFreshnessState({ stale: intel?.stale, computedAt: intel?.computed_at }),
    [intel?.stale, intel?.computed_at],
  )

  const actions = useMemo(
    () =>
      propertyId
        ? buildActions({
            propertyId,
            source,
            bookingSource,
            hasIntel,
            totalReviewCount,
            intel,
            topRisk,
            topStrength,
            platformSpread,
            bookingSofter,
            missingSecondSource,
            loyaltyStrength: analysis?.rebooking_signals?.strength || null,
          })
        : [],
    [
      propertyId,
      source,
      bookingSource,
      hasIntel,
      totalReviewCount,
      intel,
      topRisk,
      topStrength,
      platformSpread,
      bookingSofter,
      missingSecondSource,
      analysis?.rebooking_signals?.strength,
    ],
  )

  const visibleActions = showAllActions ? actions : actions.slice(0, 2)
  const primaryAction = actions[0]

  const changeItems = useMemo(
    () =>
      buildChangeItems({
        analysis,
        intel,
        latestReviewLabel,
        platformSpread,
        totalReviewCount,
      }),
    [analysis, intel, latestReviewLabel, platformSpread, totalReviewCount],
  )

  const evidenceReviews = useMemo(
    () => buildEvidenceReviews(reviews, bookingReviews, analysis),
    [reviews, bookingReviews, analysis],
  )

  const notableStrengths = (analysis?.strengths || []).slice(0, 3)
  const notableRisks = (analysis?.risks || []).slice(0, 3)
  const loyalty = analysis?.rebooking_signals
  const loyaltyTotal = loyalty ? loyalty.explicit_mentions + loyalty.implicit_signals : 0
  const loyaltyPct = totalReviewCount > 0 ? Math.round((loyaltyTotal / totalReviewCount) * 100) : 0
  const audienceLabel = analysis?.guest_types?.length ? guestTypeSummary(analysis.guest_types, 2) : ""
  const hasCategoryData = airbnbCategoryEntries(summary?.category_scores).length > 0
    || bookingCategoryEntries(bookingSummary?.category_scores).length > 0
  const momentumReadout = useMemo(
    () =>
      buildMomentumReadout({
        analysis,
        overallTrend,
        platformModels,
        topRisk,
        topStrength,
        platformSpread,
        bookingSofter,
      }),
    [analysis, overallTrend, platformModels, topRisk, topStrength, platformSpread, bookingSofter],
  )
  const loyaltyReadout = useMemo(
    () =>
      buildLoyaltyReadout({
        loyaltyPct,
        loyaltyTotal,
        strength: loyalty?.strength,
        topRisk,
        topStrength,
      }),
    [loyaltyPct, loyaltyTotal, loyalty?.strength, topRisk, topStrength],
  )
  const rankedStrengths = useMemo(
    () =>
      notableStrengths.map((strength) => {
        const signal = findThemeSignal(analysis?.themes, strength.theme)
        return {
          ...strength,
          mentionCount: Math.max(strength.evidence_count || 0, signal?.mentions || 0),
          supportNote: signal?.evidence?.[0] || null,
        }
      }),
    [notableStrengths, analysis?.themes],
  )
  const rankedRisks = useMemo(
    () =>
      notableRisks.map((risk) => {
        const signal = findThemeSignal(analysis?.themes, risk.theme)
        return {
          ...risk,
          mentionCount: signal?.mentions || 0,
          supportNote: signal?.evidence?.[0] || null,
        }
      }),
    [notableRisks, analysis?.themes],
  )
  const supportingThemes = useMemo(() => {
    const rankedKeys = new Set(
      [...notableStrengths, ...notableRisks].map((item) => normalizeThemeKey(item.theme)),
    )
    return Object.entries(analysis?.themes || {})
      .filter(([theme]) => !rankedKeys.has(normalizeThemeKey(theme)))
      .sort(([, a], [, b]) => (b.mentions || 0) - (a.mentions || 0))
      .slice(0, 3)
  }, [analysis?.themes, notableStrengths, notableRisks])

  const heroMetrics: HeroMetric[] = [
    {
      key: "score",
      label: "Reputation score",
      value: intel?.reputation_score != null ? String(intel.reputation_score) : "—",
      detail: scoreState.label,
      context: scoreState.context,
      tone: scoreState.tone,
      sectionId: "rvw-strengths",
    },
    {
      key: "airbnb",
      label: "Airbnb",
      value: airbnbAverage != null ? `${airbnbAverage.toFixed(1)}/5` : "—",
      detail: pluralize(airbnbCount, "review"),
      context: hasAirbnbData ? recencyLabelFromContext(analysis?._numeric_context, airbnbLatestAt) : "Source not connected",
      tone: platformModels.find((platform) => platform.key === "airbnb")?.trendTone || "neutral",
      sectionId: "rvw-platform",
    },
    {
      key: "booking",
      label: "Booking.com",
      value: bookingAverageRaw != null ? `${bookingAverageRaw.toFixed(1)}/10` : "—",
      detail: pluralize(bookingCount, "review"),
      context: hasBookingData
        ? `${recencyLabelFromContext(analysis?._booking_numeric_context, bookingLatestAt)}${bookingAverageNormalized != null ? ` · ${bookingAverageNormalized.toFixed(1)}/5 normalized` : ""}`
        : "Source not connected",
      tone: platformModels.find((platform) => platform.key === "booking")?.trendTone || (platformSpread != null && platformSpread >= 0.45 ? "warning" : "neutral"),
      sectionId: "rvw-platform",
    },
    {
      key: "freshness",
      label: "Analysis freshness",
      value: freshnessState.value,
      detail: freshnessState.detail,
      context: intel?.computed_at ? `Last brief run ${formatTimeAgo(intel.computed_at)}` : "No full analysis timestamp yet",
      tone: freshnessState.tone,
      sectionId: "rvw-sources",
    },
    {
      key: "momentum",
      label: "Momentum",
      value: trendLabel(overallTrend),
      detail: hasBothSources && platformSpread != null
        ? (platformSpread >= 0.45
          ? bookingSofter
            ? "Booking.com softer"
            : "Airbnb softer"
          : "Platforms aligned")
        : latestReviewLabel,
      context: momentumReadout.headline,
      tone: toneFromTrend(overallTrend),
      sectionId: "rvw-momentum",
    },
  ]

  const platformTruthHeadline = hasBothSources
    ? platformSpread != null && platformSpread >= 0.45
      ? bookingSofter
        ? "Booking.com is softer than Airbnb."
        : "Airbnb is softer than Booking.com."
      : "Airbnb and Booking.com are broadly telling the same story."
    : hasAirbnbData
      ? "Airbnb is the only connected reputation source right now."
      : hasBookingData
        ? "Booking.com is the only connected reputation source right now."
        : "No review source is connected yet."

  const platformTruthBody = analysis?.cross_platform_note
    || (hasBothSources && platformSpread != null
      ? platformSpread >= 0.45
        ? "The difference is large enough to suspect expectation mismatch or source-specific guest mix rather than a universal service decline."
        : "Trust is landing similarly across the two acquisition channels, which usually means the stay promise is consistent."
      : "Connect both Airbnb and Booking.com to compare how guest trust changes by channel.")
  const briefBody = analysis?.teaser_sentence
    || analysis?.identity
    || (intel?.fallback?.average_rating != null || blendedRating != null
      ? `Average rating sits at ${intel?.fallback?.average_rating?.toFixed(1) || blendedRating?.toFixed(1) || "—"}, with ${pluralize(totalReviewCount, "review")} currently shaping the reputation read.`
      : "Review data is connected, but the brief is still building a clearer reputation read.")

  async function handleAirbnbImport() {
    if (!propertyId || !airbnbUrlInput.trim()) return
    setError("")
    setAirbnbImporting(true)
    try {
      const response = await apiClient.importAirbnbReviews(propertyId, airbnbUrlInput.trim())
      if (!response.ok) {
        setError(response.error || "Failed to start Airbnb import.")
        return
      }
      if (response.skipped) {
        setError(`Airbnb synced ${response.last_synced_hours_ago ?? "recently"}h ago — no re-import needed.`)
        return
      }
      await refresh()
      setAirbnbUrlInput("")
    } catch {
      setError("Failed to start Airbnb import.")
    } finally {
      setAirbnbImporting(false)
    }
  }

  async function handleBookingImport() {
    if (!propertyId || !bookingUrlInput.trim()) return
    setError("")
    setBookingImporting(true)
    try {
      const response = await apiClient.importBookingReviews(propertyId, bookingUrlInput.trim())
      if (!response.ok) {
        setError(response.error || "Failed to start Booking.com import.")
        return
      }
      if (response.skipped) {
        setError(`Booking.com synced ${response.last_synced_hours_ago ?? "recently"}h ago — no re-import needed.`)
        return
      }
      await refresh()
      setBookingUrlInput("")
    } catch {
      setError("Failed to start Booking.com import.")
    } finally {
      setBookingImporting(false)
    }
  }

  async function handleAirbnbReimport() {
    if (!propertyId || !source?.source_url) return
    setError("")
    setAirbnbImporting(true)
    try {
      const response = await apiClient.importAirbnbReviews(propertyId, source.source_url)
      if (!response.ok) {
        setError(response.error || "Failed to start Airbnb re-import.")
        return
      }
      if (response.skipped) {
        setError(`Airbnb synced ${response.last_synced_hours_ago ?? "recently"}h ago — no re-import needed.`)
        return
      }
      await refresh()
    } catch {
      setError("Failed to start Airbnb re-import.")
    } finally {
      setAirbnbImporting(false)
    }
  }

  async function handleBookingReimport() {
    if (!propertyId || !bookingSource?.source_url) return
    setError("")
    setBookingImporting(true)
    try {
      const response = await apiClient.importBookingReviews(propertyId, bookingSource.source_url)
      if (!response.ok) {
        setError(response.error || "Failed to start Booking.com re-import.")
        return
      }
      if (response.skipped) {
        setError(`Booking.com synced ${response.last_synced_hours_ago ?? "recently"}h ago — no re-import needed.`)
        return
      }
      await refresh()
    } catch {
      setError("Failed to start Booking.com re-import.")
    } finally {
      setBookingImporting(false)
    }
  }

  async function handleAirbnbUrlUpdate(url: string) {
    if (!propertyId || !url.trim()) return
    setError("")
    setAirbnbImporting(true)
    try {
      const response = await apiClient.importAirbnbReviews(propertyId, url.trim())
      if (!response.ok) {
        setError(response.error || "Failed to update Airbnb URL.")
        return
      }
      if (response.skipped) {
        setError(`Airbnb synced ${response.last_synced_hours_ago ?? "recently"}h ago — no re-import needed.`)
        return
      }
      await refresh()
    } catch {
      setError("Failed to update Airbnb URL.")
    } finally {
      setAirbnbImporting(false)
    }
  }

  async function handleBookingUrlUpdate(url: string) {
    if (!propertyId || !url.trim()) return
    setError("")
    setBookingImporting(true)
    try {
      const response = await apiClient.importBookingReviews(propertyId, url.trim())
      if (!response.ok) {
        setError(response.error || "Failed to update Booking.com URL.")
        return
      }
      if (response.skipped) {
        setError(`Booking.com synced ${response.last_synced_hours_ago ?? "recently"}h ago — no re-import needed.`)
        return
      }
      await refresh()
    } catch {
      setError("Failed to update Booking.com URL.")
    } finally {
      setBookingImporting(false)
    }
  }

  async function handleRecompute() {
    setRecomputing(true)
    try {
      await forceRecompute()
    } finally {
      setRecomputing(false)
    }
  }

  async function handleAction(action: ActionItem) {
    if (action.target.type === "route") {
      navigate(action.target.href)
      return
    }
    if (action.target.type === "section") {
      document.getElementById(action.target.sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" })
      return
    }
    if (action.target.type === "recompute") {
      await handleRecompute()
      return
    }
    if (action.target.platform === "airbnb") {
      await handleAirbnbReimport()
      return
    }
    await handleBookingReimport()
  }

  const notices: NoticeItem[] = []

  if (source?.status === "running") {
    notices.push({
      key: "airbnb-running",
      tone: "warning",
      title: "Airbnb import is running",
      body: "This usually takes a few minutes. Stayframe will refresh automatically.",
    })
  }

  if (bookingSource?.status === "running") {
    notices.push({
      key: "booking-running",
      tone: "warning",
      title: "Booking.com import is running",
      body: "This usually takes a few minutes. Stayframe will refresh automatically.",
    })
  }

  if (source?.status === "stale") {
    notices.push({
      key: "airbnb-stale",
      tone: "critical",
      title: "Airbnb import looks stale",
      body: source.last_error || "The source has been running too long and likely needs a retry.",
      action: "Retry Airbnb",
      onClick: () => void handleAirbnbReimport(),
    })
  }

  if (bookingSource?.status === "stale") {
    notices.push({
      key: "booking-stale",
      tone: "critical",
      title: "Booking.com import looks stale",
      body: bookingSource.last_error || "The source has been running too long and likely needs a retry.",
      action: "Retry Booking",
      onClick: () => void handleBookingReimport(),
    })
  }

  if (source?.status === "error") {
    notices.push({
      key: "airbnb-error",
      tone: "critical",
      title: "Airbnb import failed",
      body: source.last_error || "The latest Airbnb import failed.",
      action: "Retry Airbnb",
      onClick: () => void handleAirbnbReimport(),
    })
  }

  if (bookingSource?.status === "error") {
    notices.push({
      key: "booking-error",
      tone: "critical",
      title: "Booking.com import failed",
      body: bookingSource.last_error || "The latest Booking.com import failed.",
      action: "Retry Booking",
      onClick: () => void handleBookingReimport(),
    })
  }

  if (intel?.stale) {
    notices.push({
      key: "intel-stale",
      tone: "warning",
      title: "New reviews are waiting to be analyzed",
      body: "Stayframe has fresh review data that has not been folded into the latest brief yet.",
      action: "Reanalyze now",
      onClick: () => void handleRecompute(),
    })
  }

  if (!intel?.stale && !intel?.available && !intel?.computing && totalReviewCount >= 3) {
    notices.push({
      key: "intel-missing",
      tone: "warning",
      title: "Reputation intelligence is ready to generate",
      body: "There are enough reviews to create strengths, risks, and platform synthesis.",
      action: "Generate now",
      onClick: () => void handleRecompute(),
    })
  }

  if (loading || showRitual) {
    return (
      <main className="rvw-page">
        <div className="rvw-shell">
          {showRitual ? (
            <AiLoadingCard messages={REVIEWS_AI_MESSAGES} />
          ) : (
            <div className="rvw-loading" role="status" aria-label="Loading reviews dashboard">
              <div className="rvw-loading__hero" />
              <div className="rvw-loading__grid">
                <div className="rvw-loading__panel rvw-loading__panel--large" />
                <div className="rvw-loading__stack">
                  <div className="rvw-loading__panel" />
                  <div className="rvw-loading__panel" />
                </div>
              </div>
              <div className="rvw-loading__footer" />
            </div>
          )}
        </div>
      </main>
    )
  }

  if (!propertyId) {
    return (
      <main className="rvw-page">
        <div className="rvw-shell">
          <div className="rvw-error">
            <p className="rvw-error__title">Property unavailable</p>
            <p className="rvw-error__body">Stayframe could not resolve the property context for this reviews workspace.</p>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="rvw-page">
      <div className="rvw-shell">
        {error ? (
          <div className="rvw-alert rvw-alert--critical" role="alert">
            <AlertTriangle className="h-4 w-4" />
            <p>{error}</p>
          </div>
        ) : null}

        {notices.map((notice) => (
          <div key={notice.key} className={cn("rvw-alert", notice.tone === "warning" && "rvw-alert--warning", notice.tone === "critical" && "rvw-alert--critical")}>
            {notice.tone === "warning" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />}
            <div className="rvw-alert__content">
              <p className="rvw-alert__title">{notice.title}</p>
              <p className="rvw-alert__body">{notice.body}</p>
            </div>
            {notice.action && notice.onClick ? (
              <Button variant="secondary" size="sm" onClick={notice.onClick} loading={recomputing || airbnbImporting || bookingImporting}>
                {notice.action}
              </Button>
            ) : null}
          </div>
        ))}

        {!source && !bookingSource ? (
          <section className="rvw-empty-connect">
            <div className="rvw-empty-connect__hero">
              <span className="rvw-section-kicker">Reviews</span>
              <h1 className="rvw-empty__title">Connect your review sources to open the reputation command center.</h1>
              <p className="rvw-empty__body">
                Import Airbnb and Booking.com reviews to see how guest trust is moving, what guests reliably value, and which issues are beginning to drag the stay story.
              </p>
            </div>
            <div className="rvw-source-grid">
              <SourceConnectCard
                platform="Airbnb"
                placeholder="https://www.airbnb.com/rooms/12345"
                importing={airbnbImporting}
                value={airbnbUrlInput}
                onChange={setAirbnbUrlInput}
                onImport={() => void handleAirbnbImport()}
              />
              <SourceConnectCard
                platform="Booking.com"
                placeholder="https://www.booking.com/hotel/xx/your-property.html"
                importing={bookingImporting}
                value={bookingUrlInput}
                onChange={setBookingUrlInput}
                onImport={() => void handleBookingImport()}
              />
            </div>
          </section>
        ) : null}

        {hasAnyData ? (
          <>
            <section className="rvw-hero">
              <div className="rvw-hero__content">
                <div className="rvw-hero__topbar">
                  <div className="rvw-hero__eyebrow">
                    <span
                      className={cn(
                        "rvw-stage-chip",
                        verdict.tone === "strong" && "rvw-stage-chip--positive",
                        verdict.tone === "steady" && "rvw-stage-chip--steady",
                        verdict.tone === "softening" && "rvw-stage-chip--warning",
                        verdict.tone === "mixed" && "rvw-stage-chip--mixed",
                      )}
                    >
                      {verdict.tone === "strong" ? "Strengthening" : verdict.tone === "softening" ? "Softening" : verdict.tone === "mixed" ? "Mixed signal" : "Stable"}
                    </span>
                    <span className="rvw-hero__property">{property?.name || "Property reputation"}</span>
                  </div>

                  <div className="rvw-hero__meta">
                    <span className={cn("rvw-chip", toneClass(freshnessState.tone))}>
                      <RefreshCw className="h-3.5 w-3.5" />
                      Analysis {freshnessState.value}
                    </span>
                    <span className={cn("rvw-chip", toneClass(toneFromTrend(overallTrend)))}>
                      {overallTrend === "softening" ? <TrendingDown className="h-3.5 w-3.5" /> : overallTrend === "improving" ? <TrendingUp className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />}
                      {trendLabel(overallTrend)}
                    </span>
                    {audienceLabel ? (
                      <span className="rvw-chip rvw-chip--neutral">
                        <MessageSquareText className="h-3.5 w-3.5" />
                        {audienceLabel}
                      </span>
                    ) : null}
                    <span className={cn("rvw-chip", toneClass(hasBothSources && platformSpread != null && platformSpread >= 0.45 ? "warning" : "neutral"))}>
                      <Star className="h-3.5 w-3.5" />
                      {hasBothSources && platformSpread != null ? `Platform spread ${platformSpread.toFixed(1)}` : latestReviewLabel}
                    </span>
                  </div>
                </div>

                <div className="rvw-hero__copy">
                  <h1 className="rvw-hero__headline">{verdict.headline}</h1>
                  <p className="rvw-hero__summary">{verdict.summary}</p>
                </div>

                <div className="rvw-weekly-grid">
                  <div className="rvw-panel rvw-panel--board rvw-panel--glance" id="rvw-actions">
                    <div className="rvw-panel__header">
                      <div>
                        <span className="rvw-section-kicker">Weekly check</span>
                        <h2 className="rvw-panel__title">What to do before scores slip</h2>
                      </div>
                      <div className="rvw-panel__controls">
                        <Button variant="ghost" size="sm" onClick={() => void refresh()}>
                          Refresh reviews
                        </Button>
                        {totalReviewCount >= 3 ? (
                          <Button
                            variant={recomputeCooldown ? "ghost" : "secondary"}
                            size="sm"
                            onClick={() => void handleRecompute()}
                            loading={recomputing}
                            disabled={recomputeCooldown || recomputing}
                            title={recomputeCooldown ? "Limited to once per day" : undefined}
                          >
                            {recomputeCooldown ? `Analysed ${formatRelativeTime((intel as any)?.computed_at)}` : "Reanalyze"}
                          </Button>
                        ) : null}
                      </div>
                    </div>

                    <p className="rvw-panel__body rvw-panel__body--compact">
                      {primaryAction
                        ? `Start with ${primaryAction.title}. Most weekly check-ins should stop after the first two moves below.`
                        : "Most weekly check-ins should stop after the first two moves below."}
                    </p>

                    <div className="rvw-action-list">
                      {visibleActions.map((action, index) => (
                        <article
                          key={action.key}
                          className={cn(
                            "rvw-action",
                            action.tone === "critical" && "rvw-action--critical",
                            action.tone === "warning" && "rvw-action--warning",
                            action.tone === "positive" && "rvw-action--positive",
                          )}
                        >
                          <div className="rvw-action__rank">{String(index + 1).padStart(2, "0")}</div>
                          <div className="rvw-action__body">
                            <div className="rvw-action__meta">
                              <span>{action.category}</span>
                            </div>
                            <h3 className="rvw-action__title">{action.title}</h3>
                            <p className="rvw-action__detail">{action.detail}</p>
                            <p className="rvw-action__consequence">{action.consequence}</p>
                          </div>
                          <div className="rvw-action__cta">
                            <Button
                              variant={index === 0 ? "primary" : "secondary"}
                              onClick={() => void handleAction(action)}
                              loading={
                                action.target.type === "recompute"
                                  ? recomputing
                                  : action.target.type === "reimport"
                                    ? action.target.platform === "airbnb"
                                      ? airbnbImporting
                                      : bookingImporting
                                    : false
                              }
                            >
                              {action.ctaLabel}
                            </Button>
                          </div>
                        </article>
                      ))}
                    </div>

                    {actions.length > 2 ? (
                      <div className="rvw-action-list__footer">
                        <Button variant="ghost" size="sm" onClick={() => setShowAllActions((value) => !value)}>
                          {showAllActions ? "Show fewer actions" : `Show all ${actions.length} actions`}
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  <aside className="rvw-brief">
                    <div className="rvw-brief__header">
                      <div>
                        <span className="rvw-brief__eyebrow">Reputation brief</span>
                        <h2 className="rvw-brief__title">What this pattern means</h2>
                      </div>
                      <span className="rvw-brief__meta">
                        {intel?.computing && !hasIntel ? "Updating…" : hasIntel ? "AI sharpened" : "Metric backed"}
                      </span>
                    </div>

                    <p className="rvw-brief__body">{briefBody}</p>

                    {intel?.computing && !hasIntel ? (
                      <div className="rvw-brief__loading">
                        <div />
                        <div />
                      </div>
                    ) : (
                      <div className="rvw-brief__drivers">
                        {topStrength ? (
                          <div className="rvw-brief__driver">
                            <span className="rvw-brief__driver-label">Reliable strength</span>
                            <p>{topStrength.signal}</p>
                          </div>
                        ) : null}
                        {topRisk ? (
                          <div className="rvw-brief__driver">
                            <span className="rvw-brief__driver-label">Emerging drag</span>
                            <p>{topRisk.signal}</p>
                          </div>
                        ) : null}
                        <div className="rvw-brief__driver">
                          <span className="rvw-brief__driver-label">Platform truth</span>
                          <p>{platformTruthBody}</p>
                        </div>
                      </div>
                    )}
                  </aside>
                </div>

                <div className="rvw-kpi-grid">
                  {heroMetrics.map((metric) => (
                    <button
                      key={metric.key}
                      type="button"
                      className={cn(
                        "rvw-kpi",
                        metric.sectionId && "rvw-kpi--interactive",
                        metric.tone === "positive" && "rvw-kpi--positive",
                        metric.tone === "warning" && "rvw-kpi--warning",
                        metric.tone === "critical" && "rvw-kpi--critical",
                        metric.key === "score" && "rvw-kpi--score",
                      )}
                      onClick={
                        metric.sectionId
                          ? () => {
                              const sectionId = metric.sectionId
                              if (!sectionId) return
                              document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" })
                            }
                          : undefined
                      }
                    >
                      <p className="rvw-kpi__label">{metric.label}</p>
                      <p className="rvw-kpi__value">{metric.value}</p>
                      <p className={cn("rvw-kpi__detail", metric.tone === "positive" && "rvw-kpi__detail--positive", metric.tone === "warning" && "rvw-kpi__detail--warning", metric.tone === "critical" && "rvw-kpi__detail--critical")}>
                        {metric.detail}
                      </p>
                      {metric.context ? <p className="rvw-kpi__context">{metric.context}</p> : null}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section className="rvw-deep-dive">
              <div className="rvw-deep-dive__intro">
                <div>
                  <span className="rvw-section-kicker">Investigation</span>
                  <h2 className="rvw-deep-dive__title">Why the reputation read looks this way</h2>
                </div>
                <p className="rvw-deep-dive__body">
                  Use the sections below to investigate trust gaps, repeated review themes, and where the next rating drag is likely to come from.
                </p>
              </div>

              <section className="rvw-main-grid">
                <div className="rvw-panel" id="rvw-platform">
                  <div className="rvw-panel__header">
                    <div>
                      <span className="rvw-section-kicker">Cross-platform truth</span>
                      <h2 className="rvw-panel__title">{platformTruthHeadline}</h2>
                    </div>
                  </div>
                  <p className="rvw-panel__body">{platformTruthBody}</p>

                  <div className="rvw-platform-list">
                    {platformModels.map((platform) => (
                      <div key={platform.key} className="rvw-platform-card">
                        <div className="rvw-platform-card__head">
                          <div>
                            <p className="rvw-platform-card__label">{platform.label}</p>
                            <p className="rvw-platform-card__meta">{platform.reviewLabel}</p>
                          </div>
                          <span className={cn("rvw-chip", toneClass(platform.statusTone))}>{platform.statusLabel}</span>
                        </div>
                        <div className="rvw-platform-card__rating">
                          <strong>{platform.ratingValue}</strong>
                          <span>{platform.ratingScale}</span>
                        </div>
                        <div className="rvw-platform-card__stats">
                          <span>{platform.recencyLabel}</span>
                          <span>{platform.velocityLabel}</span>
                          <span className={cn("rvw-platform-card__trend", platform.trendTone === "positive" && "rvw-platform-card__trend--positive", platform.trendTone === "warning" && "rvw-platform-card__trend--warning")}>
                            {platform.trendLabel}
                          </span>
                        </div>
                        <p className="rvw-platform-card__body">{platform.commentary}</p>
                        {platform.key === "booking" && platform.normalizedRating != null ? (
                          <p className="rvw-platform-card__note">{platform.normalizedRating.toFixed(1)}/5 normalized equivalent</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rvw-sidebar">
                  <div className="rvw-panel">
                    <div className="rvw-panel__header">
                      <div>
                        <span className="rvw-section-kicker">What changed</span>
                        <h2 className="rvw-panel__title">Movement that matters right now</h2>
                      </div>
                    </div>
                    <div className="rvw-change-list">
                      {changeItems.map((item) => (
                        <div key={item.key} className="rvw-change">
                          <div>
                            <p className="rvw-change__label">{item.label}</p>
                            <p className="rvw-change__detail">{item.detail}</p>
                          </div>
                          <span className={cn("rvw-change__value", item.tone === "positive" && "rvw-change__value--positive", item.tone === "warning" && "rvw-change__value--warning", item.tone === "critical" && "rvw-change__value--critical")}>
                            {item.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              <section className="rvw-lens-grid">
                <div className="rvw-panel" id="rvw-strengths">
                  <div className="rvw-panel__header">
                    <div>
                      <span className="rvw-section-kicker">Strengths & risks</span>
                      <h2 className="rvw-panel__title">What guests are reliably loving, and what is dragging trust</h2>
                    </div>
                  </div>

                  {hasIntel ? (
                    <>
                      <div className="rvw-strength-risk-grid">
                        <div>
                          <p className="rvw-list-title">Protect</p>
                          <div className="rvw-signal-list">
                            {rankedStrengths.length > 0 ? rankedStrengths.map((strength, index) => (
                              <div key={`${strength.theme}-${index}`} className="rvw-signal rvw-signal--positive">
                                <div className="rvw-signal__head">
                                  <p className="rvw-signal__title">{themeDisplayName(strength.theme)}</p>
                                  <span className="rvw-signal__count">{strength.mentionCount} mentions</span>
                                </div>
                                <p className="rvw-signal__body">{strength.signal}</p>
                                {strength.supportNote ? <p className="rvw-signal__note">{strength.supportNote}</p> : null}
                              </div>
                            )) : (
                              <div className="rvw-empty-inline">
                                <Sparkles className="h-4 w-4" />
                                <span>Strengths will surface once review themes are clear enough to rank.</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <p className="rvw-list-title">Fix</p>
                          <div className="rvw-signal-list">
                            {rankedRisks.length > 0 ? rankedRisks.map((risk, index) => (
                              <div
                                key={`${risk.theme}-${index}`}
                                className={cn(
                                  "rvw-signal",
                                  risk.severity === "high" && "rvw-signal--critical",
                                  risk.severity === "medium" && "rvw-signal--warning",
                                  risk.severity === "low" && "rvw-signal--neutral",
                                )}
                              >
                                <div className="rvw-signal__head">
                                  <p className="rvw-signal__title">{themeDisplayName(risk.theme)}</p>
                                  <span className="rvw-signal__count">
                                    {risk.mentionCount > 0 ? `${risk.mentionCount} mentions · ` : ""}{risk.severity} risk
                                  </span>
                                </div>
                                <p className="rvw-signal__body">{risk.signal}</p>
                                {risk.evidence ? <p className="rvw-signal__note">{risk.evidence}</p> : risk.supportNote ? <p className="rvw-signal__note">{risk.supportNote}</p> : null}
                              </div>
                            )) : (
                              <div className="rvw-empty-inline">
                                <Sparkles className="h-4 w-4" />
                                <span>No repeating risk is standing out strongly right now.</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {supportingThemes.length > 0 ? (
                        <div className="rvw-signal-summary">
                          <span className="rvw-signal-summary__label">Also recurring</span>
                          <p className="rvw-signal-summary__body">
                            {supportingThemes.map(([theme, signal]) => `${themeDisplayName(theme)} (${signal.mentions})`).join(" • ")}
                          </p>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="rvw-empty-inline">
                      <Sparkles className="h-4 w-4" />
                      <span>Full strengths and risk ranking appears once the reputation brief is generated.</span>
                    </div>
                  )}
                </div>

                <div className="rvw-sidebar">
                  <div className="rvw-panel" id="rvw-momentum">
                    <div className="rvw-panel__header">
                      <div>
                        <span className="rvw-section-kicker">Momentum</span>
                        <h2 className="rvw-panel__title">{momentumReadout.headline}</h2>
                      </div>
                    </div>
                    <p className="rvw-panel__body">{momentumReadout.summary}</p>

                    <div className="rvw-momentum-grid">
                      <div className="rvw-momentum-card">
                        <p className="rvw-momentum-card__label">Overall</p>
                        <div className="rvw-momentum-card__value">
                          {overallTrend === "improving" ? <TrendingUp className="h-4 w-4" /> : overallTrend === "softening" ? <TrendingDown className="h-4 w-4" /> : <RefreshCw className="h-4 w-4" />}
                          <strong>{trendLabel(overallTrend)}</strong>
                        </div>
                        <p className="rvw-momentum-card__detail">
                          {analysis?._numeric_context?.avg_oldest_third != null && analysis?._numeric_context?.avg_newest_third != null
                            ? `${analysis._numeric_context.avg_oldest_third.toFixed(1)} → ${analysis._numeric_context.avg_newest_third.toFixed(1)} on Airbnb`
                            : "Review quality across the latest window."}
                        </p>
                      </div>

                      {platformModels.map((platform) => (
                        <div key={platform.key} className="rvw-momentum-card">
                          <p className="rvw-momentum-card__label">{platform.label}</p>
                          <div className="rvw-momentum-card__value">
                            {platform.trendTone === "positive" ? <TrendingUp className="h-4 w-4" /> : platform.trendTone === "warning" ? <TrendingDown className="h-4 w-4" /> : <RefreshCw className="h-4 w-4" />}
                            <strong>{platform.trendLabel}</strong>
                          </div>
                          <p className="rvw-momentum-card__detail">{platform.recencyLabel} • {platform.velocityLabel}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {loyalty && loyaltyTotal > 0 ? (
                    <div className="rvw-panel rvw-panel--accent">
                      <div className="rvw-panel__header">
                        <div>
                          <span className="rvw-section-kicker">Loyalty signal</span>
                          <h2 className="rvw-panel__title">{loyaltyReadout.headline}</h2>
                        </div>
                      </div>
                      <div className="rvw-loyalty">
                        <div className="rvw-loyalty__value">
                          <strong>{loyaltyPct}%</strong>
                          <span className={cn("rvw-chip", toneClass(loyalty.strength === "strong" ? "positive" : loyalty.strength === "moderate" ? "warning" : "neutral"))}>
                            {loyalty.strength}
                          </span>
                        </div>
                        <p className="rvw-panel__body">
                          {loyaltyReadout.summary}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              </section>

              <section className="rvw-lower-grid">
              <div className="rvw-panel" id="rvw-evidence">
                <div className="rvw-panel__header">
                  <div>
                    <span className="rvw-section-kicker">Review evidence</span>
                    <h2 className="rvw-panel__title">Proof behind the reputation story</h2>
                  </div>
                </div>

                {evidenceReviews.length > 0 ? (
                  <div className="rvw-review-grid">
                    {evidenceReviews.map((review) => (
                      <article key={review.key} className="rvw-review-card">
                        <div className="rvw-review-card__head">
                          <div>
                            <p className="rvw-review-card__platform">{review.source === "airbnb" ? "Airbnb" : "Booking.com"}</p>
                            <h3 className="rvw-review-card__reviewer">{review.reviewer}</h3>
                            <p className="rvw-review-card__meta">
                              {review.date}
                              {review.meta ? ` • ${review.meta}` : ""}
                            </p>
                          </div>
                          <span className="rvw-review-card__rating">{review.rating}</span>
                        </div>
                        {review.note ? <p className="rvw-review-card__note">{review.note}</p> : null}
                        <p className="rvw-review-card__quote">“{review.quote}”</p>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="rvw-empty-inline">
                    <MessageSquareText className="h-4 w-4" />
                    <span>Written guest evidence will appear here once reviews are imported.</span>
                  </div>
                )}
              </div>
            </section>

            {hasCategoryData ? (
              <section className="rvw-panel">
                <div className="rvw-panel__header">
                  <div>
                    <span className="rvw-section-kicker">Category detail</span>
                    <h2 className="rvw-panel__title">Where the score is being won or lost</h2>
                  </div>
                </div>

                <div className="rvw-category-grid">
                  {airbnbCategoryEntries(summary?.category_scores).length > 0 ? (
                    <div className="rvw-category-card">
                      <p className="rvw-category-card__label">Airbnb</p>
                      <div className="rvw-category-list">
                        {airbnbCategoryEntries(summary?.category_scores).map((entry) => (
                          <div key={entry.label} className="rvw-category-row">
                            <div className="rvw-category-row__meta">
                              <span>{entry.label}</span>
                              <strong>{entry.score.toFixed(1)}</strong>
                            </div>
                            <div className="rvw-category-row__bar">
                              <span style={{ width: `${(entry.score / 5) * 100}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {bookingCategoryEntries(bookingSummary?.category_scores).length > 0 ? (
                    <div className="rvw-category-card">
                      <p className="rvw-category-card__label">Booking.com</p>
                      <div className="rvw-category-list">
                        {bookingCategoryEntries(bookingSummary?.category_scores).map((entry) => (
                          <div key={entry.label} className="rvw-category-row">
                            <div className="rvw-category-row__meta">
                              <span>{entry.label}</span>
                              <strong>{entry.score.toFixed(1)}</strong>
                            </div>
                            <div className="rvw-category-row__bar">
                              <span style={{ width: `${(entry.score / 10) * 100}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}
            </section>
          </>
        ) : null}

        {(source || bookingSource || hasAnyData) ? (
          <section className="rvw-panel" id="rvw-sources">
            <div className="rvw-panel__header">
              <div>
                <span className="rvw-section-kicker">Source health</span>
                <h2 className="rvw-panel__title">Import state and connected sources</h2>
              </div>
              <Button variant="ghost" size="sm" onClick={() => void refresh()}>
                Refresh source state
              </Button>
            </div>

            <div className="rvw-source-grid">
              {source ? (
                <SourceHealthCard
                  platform="Airbnb"
                  source={source}
                  reviewCount={airbnbCount}
                  importing={airbnbImporting}
                  onReimport={() => void handleAirbnbReimport()}
                  onUrlUpdate={(url) => void handleAirbnbUrlUpdate(url)}
                />
              ) : (
                <SourceConnectCard
                  platform="Airbnb"
                  placeholder="https://www.airbnb.com/rooms/12345"
                  importing={airbnbImporting}
                  value={airbnbUrlInput}
                  onChange={setAirbnbUrlInput}
                  onImport={() => void handleAirbnbImport()}
                />
              )}

              {bookingSource ? (
                <SourceHealthCard
                  platform="Booking.com"
                  source={bookingSource}
                  reviewCount={bookingCount}
                  importing={bookingImporting}
                  onReimport={() => void handleBookingReimport()}
                  onUrlUpdate={(url) => void handleBookingUrlUpdate(url)}
                />
              ) : (
                <SourceConnectCard
                  platform="Booking.com"
                  placeholder="https://www.booking.com/hotel/xx/your-property.html"
                  importing={bookingImporting}
                  value={bookingUrlInput}
                  onChange={setBookingUrlInput}
                  onImport={() => void handleBookingImport()}
                />
              )}
            </div>
          </section>
        ) : null}

        <FeedbackTrigger pageSlug="reviews" />
      </div>
    </main>
  )
}

export function ReviewsPage() {
  const { propertyId } = usePropertyContext()
  const { plan, isLoading } = usePlanContext()

  if (isLoading) {
    return (
      <ReviewsErrorBoundary>
        <div className="space-y-6 p-8">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-64 bg-muted animate-pulse rounded-lg" />
        </div>
      </ReviewsErrorBoundary>
    )
  }

  if (plan === "host") {
    return (
      <ReviewsErrorBoundary>
        <ReviewsUpgradeWall propertyId={propertyId} />
      </ReviewsErrorBoundary>
    )
  }

  return (
    <ReviewsErrorBoundary>
      <ReviewsPageFull propertyId={propertyId} />
    </ReviewsErrorBoundary>
  )
}
